// src/ui/NoteEditor.js
// Per-track note list editor.
//
// Shows the notes of the currently-selected track (as set in the
// Sequencer) in a sortable table. Each row has inline inputs for time,
// duration, pitch, and velocity, plus a × button to delete the note.
// An "Add note" button at the bottom appends a new note at the end of
// the arrangement.
//
// All edits go through the Track mutator API (addNote / updateNote /
// removeNote), which fires track._emit() → TrackManager._emit() (via
// the forwarder in TrackManager.addTrack) → Mixer and Sequencer
// re-render. So editing here updates the canvas piano-roll in real
// time and there is no extra event wiring to maintain.

import * as Tone from 'tone';
import { t, onLocaleChange } from '../i18n.js';
import { clipLibrary, ClipLibrary } from '../clips/ClipLibrary.js';

const NOTE_NAMES = ['C', 'C#', 'D', 'D#', 'E', 'F', 'F#', 'G', 'G#', 'A', 'A#', 'B'];

/** MIDI number -> "C4", "F#5", "A3" style name. */
export function midiToName(midi) {
  const n = Math.max(0, Math.min(127, Math.round(midi)));
  const name = NOTE_NAMES[n % 12];
  const oct = Math.floor(n / 12) - 1;
  return `${name}${oct}`;
}

export { NOTE_NAMES };

/**
 * Format a number of seconds as both seconds and bar:beat position at
 * the current BPM. Bar 1 = beat 0..3 (4/4 assumed). Decimals trimmed.
 *
 *   secondsToBarsBeats(1.5) -> "1.50s — bar 2 beat 1.0"
 */
function secondsToBarsBeats(sec) {
  const bpm = Tone.Transport.bpm.value || 100;
  const beats = sec * (bpm / 60); // beats since t=0
  const bar = Math.floor(beats / 4) + 1;
  const beatInBar = (beats % 4) + 1;
  return `${sec.toFixed(2)}s — bar ${bar} beat ${beatInBar.toFixed(1)}`;
}

export class NoteEditor {
  /**
   * @param {HTMLElement} rootEl
   * @param {import('../tracks/TrackManager.js').TrackManager} trackManager
   * @param {import('../sequencer/Sequencer.js').Sequencer} sequencer
   */
  constructor(rootEl, trackManager, sequencer) {
    this.root = rootEl;
    this.tm = trackManager;
    this.sequencer = sequencer;
    this.render();

    // Re-render on any track/notes change.
    this._unsubTm = this.tm.onChange(() => this.render());
    // Re-render on selection change.
    this._unsubSel = this.sequencer.onSelectionChange(() => this.render());
    // Re-render on clip selection change (shows clip notes).
    this._unsubClipSel = this.sequencer.onClipSelectionChange(() => this.render());
    // Re-render when clip definitions change (notes added/removed/edited).
    this._unsubClipLib = clipLibrary.onChange(() => {
      if (this.sequencer.selectedClipDefId) this.render();
    });
    // Re-render on locale change to translate column headers and labels.
    onLocaleChange(() => this.render());
  }

  render() {
    this.root.innerHTML = '';
    const clipDefId = this.sequencer.selectedClipDefId;
    const clipDef = clipDefId ? clipLibrary.getClip(clipDefId) : null;
    // Only look up the track when we need it — skip when viewing clip notes.
    const track = clipDef ? null : this.selectedTrack();

    if (!track && !clipDef) {
      const empty = document.createElement('div');
      empty.className = 'note-editor-empty';
      empty.textContent = t('notes.empty');
      this.root.appendChild(empty);
      return;
    }

    if (clipDef) {
      this._renderClipNotes(clipDef);
    } else {
      this._renderTrackNotes(track);
    }
  }

  /** Render notes table for a track. */
  _renderTrackNotes(track) {
    this._renderHeader(track.name, track.color, track.notes.length, false, () => this._addNote(track));

    const tableWrap = document.createElement('div');
    tableWrap.className = 'note-editor-table-wrap';
    const table = document.createElement('table');
    table.className = 'note-editor-table';
    table.innerHTML = `
      <thead>
        <tr>
          <th>${t('notes.colIndex')}</th>
          <th>${t('notes.colPitch')}</th>
          <th>${t('notes.colTime')}</th>
          <th>${t('notes.colDuration')}</th>
          <th>${t('notes.colVelocity')}</th>
          <th>${t('notes.colActions')}</th>
        </tr>
      </thead>
      <tbody></tbody>
    `;
    const tbody = table.querySelector('tbody');
    const sorted = [...track.notes].sort((a, b) => a.time - b.time);
    sorted.forEach((n, i) => {
      tbody.appendChild(this._buildRow('track', track, null, n, i + 1));
    });
    tableWrap.appendChild(table);
    this.root.appendChild(tableWrap);
  }

  /** Render notes table for a clip definition. */
  _renderClipNotes(clipDef) {
    this._renderHeader(clipDef.name, '#5fa86a', clipDef.notes.length, true, () => this._addClipNote(clipDef));

    // Action buttons: Edit (opens ClipEditor canvas), Duplicate instance on track,
    // Delete instance from track.
    const actions = document.createElement('div');
    actions.className = 'clip-editor-actions';
    const editBtn = document.createElement('button');
    editBtn.className = 'clip-editor-action-btn';
    editBtn.textContent = '✎ Edit';
    editBtn.addEventListener('click', () => {
      if (this.sequencer._emitClipEdit) this.sequencer._emitClipEdit(clipDef.id);
    });

    const dupBtn = document.createElement('button');
    dupBtn.className = 'clip-editor-action-btn';
    dupBtn.textContent = '⧉ Duplicate';
    dupBtn.addEventListener('click', () => {
      // Re-resolve the instance at click time, not from a stale render capture.
      const curTrack = this.selectedTrack();
      const curInst = curTrack
        ? curTrack.clips.find((c) => c.id === this.sequencer.selectedClipInstId)
        : null;
      if (curInst) {
        const clipDur = ClipLibrary.getDuration(clipDef.notes);
        curTrack.addClipInstance(clipDef.id, curInst.startTime + clipDur, curInst.repeatCount);
      }
    });

    const delBtn = document.createElement('button');
    delBtn.className = 'clip-editor-action-btn danger';
    delBtn.textContent = '✕ Delete';
    delBtn.addEventListener('click', () => {
      const curTrack = this.selectedTrack();
      if (curTrack) {
        curTrack.removeClipInstance(this.sequencer.selectedClipInstId);
        this.sequencer.setSelectedClip(null, null);
      }
    });

    actions.appendChild(editBtn);
    actions.appendChild(dupBtn);
    actions.appendChild(delBtn);
    this.root.appendChild(actions);

    const tableWrap = document.createElement('div');
    tableWrap.className = 'note-editor-table-wrap';
    const table = document.createElement('table');
    table.className = 'note-editor-table';
    table.innerHTML = `
      <thead>
        <tr>
          <th>${t('notes.colIndex')}</th>
          <th>${t('notes.colPitch')}</th>
          <th>${t('notes.colTime')}</th>
          <th>${t('notes.colDuration')}</th>
          <th>${t('notes.colVelocity')}</th>
          <th>${t('notes.colActions')}</th>
        </tr>
      </thead>
      <tbody></tbody>
    `;
    const tbody = table.querySelector('tbody');
    const sorted = [...clipDef.notes].sort((a, b) => a.time - b.time);
    sorted.forEach((n, i) => {
      tbody.appendChild(this._buildRow('clip', null, clipDef, n, i + 1));
    });
    tableWrap.appendChild(table);
    this.root.appendChild(tableWrap);
  }

  /** Shared header rendering. addNoteFn is called when the "+" button is clicked. */
  _renderHeader(name, color, noteCount, isClip = false, addNoteFn = null) {
    const header = document.createElement('div');
    header.className = 'note-editor-header';
    const swatch = document.createElement('span');
    swatch.className = 'note-editor-swatch';
    swatch.style.setProperty('--swatch-color', color);
    const title = document.createElement('span');
    title.className = 'note-editor-title';
    title.textContent = name;
    const meta = document.createElement('span');
    meta.className = 'note-editor-meta';
    meta.textContent = t('notes.count', noteCount);
    header.appendChild(swatch);
    header.appendChild(title);
    header.appendChild(meta);
    if (addNoteFn) {
      const addBtn = document.createElement('button');
      addBtn.className = 'note-editor-add';
      addBtn.textContent = '+' + t('notes.addBtn');
      addBtn.addEventListener('click', addNoteFn);
      header.appendChild(addBtn);
    }
    if (isClip) {
      const backBtn = document.createElement('button');
      backBtn.className = 'note-editor-back-btn';
      backBtn.textContent = '← Track';
      backBtn.title = 'Back to track notes';
      backBtn.addEventListener('click', () => {
        this.sequencer.setSelectedClip(null, null);
      });
      header.appendChild(backBtn);
    }
    this.root.appendChild(header);
  }

  _buildRow(mode, track, clipDef, note, index) {
    const tr = document.createElement('tr');
    tr.className = 'note-editor-row';
    tr.dataset.noteId = note.id;

    const isClip = mode === 'clip';

    const tdIndex = document.createElement('td');
    tdIndex.className = 'note-editor-cell-index';
    tdIndex.textContent = String(index);
    tr.appendChild(tdIndex);

    // Pitch: numeric input + note-name label that updates live.
    const tdPitch = document.createElement('td');
    const pitchInput = document.createElement('input');
    pitchInput.type = 'number';
    pitchInput.min = 0;
    pitchInput.max = 127;
    pitchInput.step = 1;
    pitchInput.value = String(note.pitch);
    pitchInput.className = 'note-editor-input note-editor-input-pitch';
    pitchInput.title = t('notes.pitchTitle');
    pitchInput.addEventListener('change', () => {
      const v = Math.max(0, Math.min(127, parseInt(pitchInput.value, 10) || 0));
      if (isClip) clipLibrary.updateClipNote(clipDef.id, note.id, { pitch: v });
      else track.updateNote(note.id, { pitch: v });
    });
    const pitchName = document.createElement('span');
    pitchName.className = 'note-editor-pitch-name';
    pitchName.textContent = midiToName(note.pitch);
    tdPitch.appendChild(pitchInput);
    tdPitch.appendChild(pitchName);
    pitchInput.addEventListener('input', () => {
      const v = Math.max(0, Math.min(127, parseInt(pitchInput.value, 10) || 0));
      pitchName.textContent = midiToName(v);
    });
    tr.appendChild(tdPitch);

    // Time: numeric input (seconds) + bar:beat label.
    const tdTime = document.createElement('td');
    const timeInput = document.createElement('input');
    timeInput.type = 'number';
    timeInput.min = 0;
    timeInput.step = 0.05;
    timeInput.value = note.time.toFixed(2);
    timeInput.className = 'note-editor-input note-editor-input-time';
    timeInput.title = t('notes.timeTitle');
    timeInput.addEventListener('change', () => {
      const v = Math.max(0, parseFloat(timeInput.value) || 0);
      if (isClip) clipLibrary.updateClipNote(clipDef.id, note.id, { time: v });
      else track.updateNote(note.id, { time: v });
    });
    const timeLabel = document.createElement('span');
    timeLabel.className = 'note-editor-time-label';
    timeLabel.textContent = secondsToBarsBeats(note.time);
    tdTime.appendChild(timeInput);
    tdTime.appendChild(timeLabel);
    tr.appendChild(tdTime);

    // Duration
    const tdDur = document.createElement('td');
    const durInput = document.createElement('input');
    durInput.type = 'number';
    durInput.min = 0.05;
    durInput.step = 0.05;
    durInput.value = note.duration.toFixed(2);
    durInput.className = 'note-editor-input';
    durInput.title = t('notes.durationTitle');
    durInput.addEventListener('change', () => {
      const v = Math.max(0.05, parseFloat(durInput.value) || 0.05);
      if (isClip) clipLibrary.updateClipNote(clipDef.id, note.id, { duration: v });
      else track.updateNote(note.id, { duration: v });
    });
    tdDur.appendChild(durInput);
    tr.appendChild(tdDur);

    // Velocity (0..1)
    const tdVel = document.createElement('td');
    const velInput = document.createElement('input');
    velInput.type = 'number';
    velInput.min = 0;
    velInput.max = 1;
    velInput.step = 0.05;
    velInput.value = note.velocity.toFixed(2);
    velInput.className = 'note-editor-input note-editor-input-vel';
    velInput.title = t('notes.velocityTitle');
    velInput.addEventListener('change', () => {
      const v = Math.max(0, Math.min(1, parseFloat(velInput.value) || 0));
      if (isClip) clipLibrary.updateClipNote(clipDef.id, note.id, { velocity: v });
      else track.updateNote(note.id, { velocity: v });
    });
    tdVel.appendChild(velInput);
    tr.appendChild(tdVel);

    // Delete
    const tdX = document.createElement('td');
    const x = document.createElement('button');
    x.className = 'note-editor-x';
    x.textContent = '×';
    x.title = t('notes.deleteTitle');
    x.addEventListener('click', () => {
      if (isClip) clipLibrary.removeClipNote(clipDef.id, note.id);
      else track.removeNote(note.id);
    });
    tdX.appendChild(x);
    tr.appendChild(tdX);

    return tr;
  }

  _addNote(track) {
    // Place the new note after the last existing one (or at t=0).
    let t = 0;
    if (track.notes.length > 0) {
      const lastEnd = track.notes.reduce((mx, n) => Math.max(mx, n.time + n.duration), 0);
      t = lastEnd + 0.25;
    }
    const newNote = track.addNote({ time: t, duration: 0.5, pitch: 60, velocity: 0.8 });
    this.sequencer.selectedNoteId = newNote.id;
  }

  _addClipNote(clipDef) {
    let t = 0;
    if (clipDef.notes.length > 0) {
      const lastEnd = clipDef.notes.reduce((mx, n) => Math.max(mx, n.time + n.duration), 0);
      t = lastEnd + 0.25;
    }
    clipLibrary.addClipNote(clipDef.id, { time: t, duration: 0.5, pitch: 60, velocity: 0.8 });
  }

  selectedTrack() {
    const id = this.sequencer.selectedTrackId;
    if (!id) return null;
    return this.tm.getTrack(id);
  }
}
